import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useSelector } from "react-redux";
import {
  type SMTableData,
  columns as smTableColumns,
} from "../SMTableColumns";
import { SelectCell } from "./SelectCell";
import { getStickyColumnStyle } from "./usePtpColumns";
import { SELECT_WIDTH } from "./constants";
import { buildCustomFieldColumns } from "../CustomFieldColumns";
import { useCustomFieldDefinitions } from "src/hooks/useCustomFields";

const EMPTY_FIELDS: any[] = [];

// Module scope: createColumnHelper() holds no per-render state, and a fresh helper
// each render would destabilise the columns memo below (see useSiteColumns).
const columnHelper = createColumnHelper<SMTableData>();

export function useSubscriberColumns(pinnedColumn: string) {
  const prefs = useSelector((state: any) => state.mainFrame.prefs);
  const projectId = useSelector((state: any) => state.mainFrame.projectId);
  const { data: allDefs } = useCustomFieldDefinitions(projectId);
  const customFieldDefs = allDefs?.subscriber ?? EMPTY_FIELDS;

  const customColumns = React.useMemo(
    () =>
      buildCustomFieldColumns(customFieldDefs, {
        choicesKind: 'subscriber',
        aggregationFn: 'firstChild',
      }),
    [customFieldDefs]
  );

  const columns = React.useMemo(() => {
    // the name column is dropped when something else is the pinned column.
    // Filtering inside the memo keeps it off the dependency list, where a new
    // array each render would destabilise the columns.
    const allAvailableColumns =
      pinnedColumn === 'name'
        ? smTableColumns
        : smTableColumns.filter((column) => column.id !== 'name');

    return [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => <SelectCell kind="header" table={table} />,
        cell: ({ row, table }) => <SelectCell kind="row" row={row} table={table} />,
        enableResizing: false,
        enableHiding: false,
        meta: {
          tdStyle: getStickyColumnStyle(SELECT_WIDTH, 0),
        },
      }),
      // TODO probably better to do this with mutation and findIndex
      // since we only want to modify the name column
      ...allAvailableColumns.map((column) => ({
        ...column,
        meta: {
          ...column.meta,
          prefs,
          tdStyle:
            // don't use pinnedColumn here because the header wont be sticky
            column.id === 'name'
              ? getStickyColumnStyle(null, SELECT_WIDTH)
              : column.meta?.tdStyle,
        },
        enableGrouping: column.id === pinnedColumn,
      })),
      ...customColumns,
    ];
  }, [prefs, pinnedColumn, customColumns]);

  return { columns };
}
